import { useState } from "react";
import { api } from "../lib/api";
import { AriaOrb } from "./AriaOrb";
import { useSpeech } from "../hooks/useSpeech";
import { X, Send, Loader2, Sparkles, Youtube, StickyNote, BookOpen, FileText, Volume2, Square, Headphones } from "lucide-react";
import { toast } from "sonner";

const SOURCE_ICON = { video: Youtube, note: StickyNote, turbolearn: BookOpen, paper: FileText };
const QUICK = [
  ["explain", "Explain"], ["simplify", "Simplify"], ["why", "Why it matters"], ["quiz", "Quiz me"],
];

export function PassagePanel({ documentId, passage, chunkIndex, onClose }) {
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(null);
  const [thread, setThread] = useState([]);
  const { toggle, speakSequence, stop, speaking, activeKey, loadingKey } = useSpeech();

  const SpeakBtn = ({ text, k, label = "Listen", testid }) => (
    <button onClick={() => toggle(text, k)} data-testid={testid}
      className={`text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border transition-colors ${speaking && activeKey === k ? "border-[var(--aria-primary)] text-[var(--aria-primary)]" : "border-[var(--aria-border)] text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]"}`}>
      {loadingKey === k ? <Loader2 size={11} className="animate-spin" /> : speaking && activeKey === k ? <Square size={11} /> : <Volume2 size={11} />}
      {speaking && activeKey === k ? "Stop" : label}
    </button>
  );

  const listenAll = () => {
    const last = thread[thread.length - 1];
    const items = [[passage, "passage"]];
    if (last) items.push([last.answer, `answer-${thread.length - 1}`]);
    speakSequence(items);
  };

  const ask = async ({ quick, text }) => {
    if (busy) return;
    setBusy(quick || "custom");
    try {
      const { data } = await api.post("/passage/ask", { document_id: documentId, passage, chunk_index: chunkIndex, quick, question: text });
      setThread(t => [...t, data]);
      setQuestion("");
    } catch (e) { toast.error(e?.response?.data?.detail || "ARIA couldn't answer"); }
    finally { setBusy(null); }
  };

  return (
    <aside className="w-full sm:w-[380px] shrink-0 border-l border-[var(--aria-border)] bg-[var(--aria-bg)] flex flex-col h-full" data-testid="passage-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--aria-border)]">
        <div className="flex items-center gap-2">
          <AriaOrb size={22} state={busy ? "thinking" : "idle"} />
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[var(--aria-primary)]">Ask about this passage</span>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={speaking ? stop : listenAll} data-testid="passage-listen-all" title="Read passage and latest explanation aloud"
            className={`text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border ${speaking ? "border-[var(--aria-primary)] text-[var(--aria-primary)]" : "border-[var(--aria-border)] text-[var(--aria-text-muted)] hover:text-[var(--aria-text)]"}`}>
            {speaking ? <Square size={11} /> : <Headphones size={11} />} {speaking ? "Stop" : "Read aloud"}
          </button>
          <button onClick={() => { stop(); onClose(); }} data-testid="passage-panel-close" className="p-1 rounded hover:bg-[var(--aria-surface-2)]"><X size={16} /></button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div>
          <blockquote className="text-sm italic text-[var(--aria-text-muted)] border-l-2 border-[var(--aria-accent)] pl-3 line-clamp-5" data-testid="passage-quote">“{passage}”</blockquote>
          <div className="mt-2 pl-3"><SpeakBtn text={passage} k="passage" label="Listen to passage" testid="passage-listen-quote" /></div>
        </div>

        <div className="flex flex-wrap gap-2" data-testid="passage-quick-actions">
          {QUICK.map(([k, label]) => (
            <button key={k} onClick={() => ask({ quick: k })} disabled={!!busy} data-testid={`passage-quick-${k}`}
              className="aria-pill text-xs px-3 py-1.5 hover:border-[var(--aria-primary)] disabled:opacity-50 inline-flex items-center gap-1.5">
              {busy === k ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} className="text-[var(--aria-primary)]" />} {label}
            </button>
          ))}
        </div>

        {thread.map((t, i) => (
          <div key={i} className="space-y-2 fade-in" data-testid={`passage-answer-${i}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs text-[var(--aria-text-muted)]">{t.question}</div>
              <SpeakBtn text={t.answer} k={`answer-${i}`} testid={`passage-listen-answer-${i}`} />
            </div>
            <div className="text-sm leading-relaxed whitespace-pre-line rounded-md bg-[var(--aria-surface)] border border-[var(--aria-border)] px-3.5 py-3">{t.answer}</div>
            {t.sources?.length > 0 && (
              <div className="space-y-1">
                {t.sources.map((s, j) => {
                  const Icon = SOURCE_ICON[s.source_type] || FileText;
                  return (
                    <div key={j} className="flex items-center gap-2 text-[11px] text-[var(--aria-text-muted)]">
                      <Icon size={11} className="text-[var(--aria-primary)]" /><span className="uppercase tracking-wide">{s.source_type}</span><span className="truncate">{s.source_title}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
        {thread.length === 0 && !busy && (
          <div className="text-xs text-[var(--aria-text-muted)]">Pick a quick action or type your own question. ARIA answers from this paper and your other connected material.</div>
        )}
        {busy && <div className="text-xs text-[var(--aria-text-muted)] flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Reading the passage…</div>}
      </div>

      <div className="p-3 border-t border-[var(--aria-border)] flex gap-2">
        <input value={question} onChange={e => setQuestion(e.target.value)} placeholder="Ask anything about this passage…"
          onKeyDown={e => { if (e.key === "Enter" && question.trim()) ask({ text: question }); }}
          data-testid="passage-question-input"
          className="flex-1 px-3 py-2 rounded-md border border-[var(--aria-border)] bg-[var(--aria-surface)] outline-none focus:ring-2 focus:ring-[var(--aria-primary)] text-sm" />
        <button onClick={() => question.trim() && ask({ text: question })} disabled={!!busy || !question.trim()} data-testid="passage-question-submit"
          className="btn btn-primary px-3 disabled:opacity-40 flex items-center"><Send size={14} /></button>
      </div>
    </aside>
  );
}
